import { Component } from '@theme/component';

const STORAGE_KEY = 'dob:wishlist';
const CHANGE_EVENT = 'wishlist:change';

/**
 * Reads saved product handles.
 *
 * Storage is unavailable in private browsing and when site data is blocked, so every
 * access is guarded and an empty wishlist is the fallback rather than a thrown error.
 *
 * @returns {string[]}
 */
function readHandles() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((handle) => typeof handle === 'string') : [];
  } catch {
    return [];
  }
}

/** @param {string[]} handles */
function writeHandles(handles) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch {
    // Persistence is best-effort; the in-page state below still updates.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
}

/**
 * @param {string} handle
 * @returns {boolean} Whether the handle is saved after toggling.
 */
function toggleHandle(handle) {
  const handles = readHandles();
  const index = handles.indexOf(handle);

  if (index === -1) {
    handles.push(handle);
  } else {
    handles.splice(index, 1);
  }

  writeHandles(handles);
  return index === -1;
}

/**
 * A save/remove toggle for a single product.
 *
 * @extends {Component<{}>}
 */
class WishlistButton extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener(CHANGE_EVENT, this.#sync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  get #handle() {
    return this.getAttribute('data-product-handle') ?? '';
  }

  toggle(event) {
    event.preventDefault();
    event.stopPropagation();
    if (!this.#handle) return;
    toggleHandle(this.#handle);
  }

  #sync = () => {
    const button = this.querySelector('button');
    if (!button) return;

    const saved = readHandles().includes(this.#handle);
    button.setAttribute('aria-pressed', String(saved));
    button.setAttribute('aria-label', saved ? button.dataset.labelSaved ?? '' : button.dataset.labelSave ?? '');
    this.toggleAttribute('data-saved', saved);
  };
}

/**
 * The header indicator showing how many products are saved.
 *
 * @extends {Component<{}>}
 */
class WishlistCount extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#sync();
    document.addEventListener(CHANGE_EVENT, this.#sync);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#sync);
  }

  #sync = () => {
    const count = readHandles().length;
    this.textContent = count > 0 ? String(count) : '';
    this.toggleAttribute('hidden', count === 0);
  };
}

/**
 * Renders saved products by requesting one server-rendered card per handle, so the
 * wishlist grid stays identical to the cards used everywhere else in the theme.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} grid
 * @property {HTMLElement} empty
 *
 * @extends {Component<Refs>}
 */
class WishlistList extends Component {
  requiredRefs = ['grid', 'empty'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(CHANGE_EVENT, this.#onChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CHANGE_EVENT, this.#onChange);
    this.#abortController.abort();
  }

  #onChange = () => {
    // A removal from within the grid only needs the emptied card dropped, but a change
    // from another tab or page can add handles, so re-render from storage either way.
    this.#render();
  };

  async #render() {
    const handles = readHandles();
    const { grid, empty } = this.refs;

    if (handles.length === 0) {
      grid.replaceChildren();
      grid.hidden = true;
      empty.hidden = false;
      return;
    }

    empty.hidden = true;
    grid.hidden = false;

    this.#abortController.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    const sectionId = this.getAttribute('data-card-section') ?? 'wishlist-card';

    const cards = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`${Theme.routes.root_url}products/${handle}?section_id=${sectionId}`, {
            signal,
          });
          if (!response.ok) return null;
          return { handle, html: await response.text() };
        } catch {
          return null;
        }
      })
    );

    if (signal.aborted) return;

    const items = cards.filter(Boolean).map(({ handle, html }) => {
      const item = document.createElement('li');
      item.className = 'product-grid__item';
      item.dataset.productHandle = handle;
      item.innerHTML = html;
      return item;
    });

    // Every saved handle failed to resolve — treat it as empty rather than showing a blank grid.
    if (items.length === 0) {
      grid.replaceChildren();
      grid.hidden = true;
      empty.hidden = false;
      return;
    }

    grid.replaceChildren(...items);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}

if (!customElements.get('wishlist-list')) {
  customElements.define('wishlist-list', WishlistList);
}
